import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface LogActivityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactId: string;
  contactName?: string;
}

export const LogActivityDialog = ({ open, onOpenChange, contactId, contactName }: LogActivityDialogProps) => {
  const [activityType, setActivityType] = useState<string>("note");
  const [direction, setDirection] = useState<string>("outgoing");
  const [duration, setDuration] = useState<string>("");
  const [subject, setSubject] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const metadata: Record<string, any> = {};
      
      if (activityType === "phone_call" && duration) {
        metadata.duration = parseInt(duration);
        metadata.direction = direction;
      }
      
      if ((activityType === "email" || activityType === "meeting") && subject) {
        metadata.subject = subject;
      }
      
      if (activityType === "email") {
        metadata.direction = direction;
      }

      const { error } = await supabase
        .from('crm_activities')
        .insert({
          contact_id: contactId,
          activity_type: activityType,
          description: description || `${activityType.replace('_', ' ')} logged`,
          metadata: Object.keys(metadata).length > 0 ? metadata : null,
        });

      if (error) throw error;

      toast({
        title: "Activity logged",
        description: `${activityType.replace('_', ' ')} has been recorded for ${contactName || 'this contact'}.`,
      });

      // Reset form
      setActivityType("note");
      setDirection("outgoing");
      setDuration("");
      setSubject("");
      setDescription("");
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Error logging activity",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Log Activity</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="activityType">Activity Type</Label>
            <Select value={activityType} onValueChange={setActivityType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="email">📧 Email</SelectItem>
                <SelectItem value="phone_call">📞 Phone Call</SelectItem>
                <SelectItem value="meeting">🤝 Meeting</SelectItem>
                <SelectItem value="note">📝 Note</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {(activityType === "email" || activityType === "phone_call") && (
            <div className="space-y-2">
              <Label htmlFor="direction">Direction</Label>
              <Select value={direction} onValueChange={setDirection}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="outgoing">Outgoing</SelectItem>
                  <SelectItem value="incoming">Incoming</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {activityType === "phone_call" && (
            <div className="space-y-2">
              <Label htmlFor="duration">Duration (minutes)</Label>
              <Input
                id="duration"
                type="number"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                placeholder="Optional"
                min="1"
              />
            </div>
          )}

          {(activityType === "email" || activityType === "meeting") && (
            <div className="space-y-2">
              <Label htmlFor="subject">
                {activityType === "email" ? "Email Subject" : "Meeting Title"}
              </Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={activityType === "email" ? "RE: Follow up" : "Strategy discussion"}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add notes about this activity..."
              rows={4}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Logging..." : "Log Activity"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
